import { OrganizationContext } from '../../../components/contexts/organization';
import { SearchFormValues } from '../list/List';
import {
  PollShopCouponMasterUsageJsonResponse,
  useInvokeShopCouponMasterUsageCsvLazyQuery,
  useInvokeShopCouponMasterUsageJsonLazyQuery,
  usePollShopCouponMasterUsageCsvLazyQuery,
  usePollShopCouponMasterUsageJsonLazyQuery,
} from '../../../graphql/generated';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import Bugsnag from '@bugsnag/js';
import { formatJstDate } from '../../../util';
import { downloadCsvFileWithBom } from '../../../util/download';

const Limit = 25;

export type CouponUsageStatistic = {
  organization?: OrganizationContext;
  loading: boolean;
  onSearch: (condition: SearchFormValues) => void;
  onCsvOut: (condition: SearchFormValues) => void;
  csvLoading: boolean;
  data?: PollShopCouponMasterUsageJsonResponse;
  onFetchMore: (condition: SearchFormValues) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  allAcquired: boolean;
};

export const useCouponUsageStatistic = (): CouponUsageStatistic => {
  const orgContext = useContext(OrganizationContext);
  const organization = orgContext.organization;
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [csvLoading, setCsvLoading] = useState<boolean>(false);
  const [offset, setOffset] = useState<number>(0);
  const { enqueueSnackbar } = useSnackbar();
  const [fetching, setFetching] = useState<boolean>(false);
  const [allAcquired, setAllAcquired] = useState<boolean>(false);

  const handleError = (): void => {
    setLoading(false);
    setCsvLoading(false);
    enqueueSnackbar('サーバーエラーが発生した', {
      variant: 'error',
      autoHideDuration: 3000,
    });
  };

  const [pollJsonQuery, statusContext] = usePollShopCouponMasterUsageJsonLazyQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first', // これをつけないと fetchMore 時に nextTokenなしで取得しにいってしまう
    pollInterval: 5000,
  });

  // 利用状況集計 取得
  // ApolloClient で ポーリングとfetchMoreを同時に利用できないため分ける
  const [getJsonQuery, getJsonQueryContext] = usePollShopCouponMasterUsageJsonLazyQuery({
    fetchPolicy: 'cache-first',
    onCompleted: (data) => {
      if (Number(data?.pollShopCouponMasterUsageJson?.nodes?.length) < Limit) {
        setAllAcquired(true);
      }
      setLoading(false);
    },
    onError: () => handleError(),
  });

  const [invokeJsonQuery] = useInvokeShopCouponMasterUsageJsonLazyQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    onCompleted: (data) => {
      pollJsonQuery({
        variables: {
          queryExecutionId: data.invokeShopCouponMasterUsageJson.queryExecutionId,
        },
      });
      setOffset(offset + Limit);
    },
    onError: () => handleError(),
  });

  // 利用状況一覧 CSV URL ポーリング
  const [pollCsvUrlQuery, statusCsvUrlContext] = usePollShopCouponMasterUsageCsvLazyQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    pollInterval: 5000,
  });

  // 利用状況CSV取得 invoke
  const [invokeCsvQuery] = useInvokeShopCouponMasterUsageCsvLazyQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    onCompleted: (data) => {
      pollCsvUrlQuery({
        variables: {
          queryExecutionId: data.invokeShopCouponMasterUsageCsv.queryExecutionId,
        },
      });
    },
    onError: () => handleError(),
  });

  // 検索実行
  const onSearch = useCallback(
    (data: SearchFormValues) => {
      if (!organization) {
        return;
      }
      setOffset(0);
      setLoading(true);
      invokeJsonQuery({
        variables: {
          usageStartDate: data.usageStartDate ? formatJstDate(new Date(data.usageStartDate).toISOString()) : undefined,
          usageEndDate: data.usageEndDate ? formatJstDate(new Date(data.usageEndDate).toISOString()) : undefined,
          limit: Limit,
          offset: 0,
        },
      });
    },
    [invokeJsonQuery]
  );

  // CSV出力
  const onCsvOutSubmit = useCallback(
    (data: SearchFormValues) => {
      if (!organization) {
        return;
      }
      setCsvLoading(true);
      invokeCsvQuery({
        variables: {
          usageStartDate: data.usageStartDate ? formatJstDate(new Date(data.usageStartDate).toISOString()) : undefined,
          usageEndDate: data.usageEndDate ? formatJstDate(new Date(data.usageEndDate).toISOString()) : undefined,
        },
      });
    },
    [organization, invokeCsvQuery]
  );

  // JSONクエリ出力をポーリングする
  // ポーリングクエリの onComplete は呼ばれないので useEffect でやるしかない
  useEffect(() => {
    const status = statusContext?.data?.pollShopCouponMasterUsageJson?.status;
    const hasError = statusContext?.error;

    // 終了系ステータスの場合、ポーリングを止める
    if (status === 'CANCELLED' || status === 'FAILED' || status === 'SUCCEEDED' || hasError) {
      if (statusContext && statusContext.stopPolling) {
        statusContext.stopPolling();
      }
    }

    // 正常に終了した場合は、データフェッチ用のクエリを呼び出してステータスを終了にする
    if (
      status === 'SUCCEEDED' &&
      statusContext &&
      statusContext.data &&
      statusContext.data.pollShopCouponMasterUsageJson &&
      statusContext.data.pollShopCouponMasterUsageJson.nodes
    ) {
      if (
        fetching &&
        organization &&
        getJsonQueryContext &&
        getJsonQueryContext.fetchMore &&
        getJsonQueryContext.variables
      ) {
        setLoading(true);
        getJsonQueryContext.fetchMore({
          variables: statusContext.variables,
          updateQuery: (prevQuery, { fetchMoreResult }) => {
            setLoading(false);
            if (!fetchMoreResult) {
              return prevQuery;
            }

            // 取得件数が0件の場合は、全件取得済みとみなす
            if (Number(fetchMoreResult?.pollShopCouponMasterUsageJson?.nodes?.length) < Limit) {
              setAllAcquired(true);
              enqueueSnackbar('全件取得済みです', { variant: 'success' });
            } else {
              enqueueSnackbar('集計処理が正常に終了しました', { variant: 'success' });
            }
            return {
              pollShopCouponMasterUsageJson: {
                __typename: fetchMoreResult.pollShopCouponMasterUsageJson.__typename,
                queryExecutionId: fetchMoreResult.pollShopCouponMasterUsageJson.queryExecutionId,
                status: fetchMoreResult.pollShopCouponMasterUsageJson.status,
                nodes: [
                  ...(prevQuery.pollShopCouponMasterUsageJson.nodes || []),
                  ...(fetchMoreResult.pollShopCouponMasterUsageJson.nodes || []),
                ],
                pageInfo: fetchMoreResult.pollShopCouponMasterUsageJson.pageInfo,
              },
            };
          },
        });
        setFetching(false);
      } else {
        getJsonQuery({
          variables: statusContext.variables,
        });
        enqueueSnackbar('集計処理が正常に終了しました', { variant: 'success' });
      }
    } else if (status === 'FAILED' || status === 'CANCELLED' || hasError) {
      setLoading(false);
      enqueueSnackbar('集計処理でエラーが発生しました', {
        variant: 'error',
      });
      Bugsnag.notify(statusContext.error || new Error('polling error'));
    }
  }, [statusContext.data?.pollShopCouponMasterUsageJson.status, statusContext?.error]);

  // CSV URL クエリ出力をポーリングする
  // ポーリングクエリの onComplete は呼ばれないので useEffect でやるしかない
  useEffect(() => {
    const status = statusCsvUrlContext?.data?.pollShopCouponMasterUsageCsv?.status;
    const hasError = statusCsvUrlContext?.error;

    // 終了系ステータスの場合、ポーリングを止める
    if (status === 'CANCELLED' || status === 'FAILED' || status === 'SUCCEEDED' || hasError) {
      if (statusCsvUrlContext && statusCsvUrlContext.stopPolling) {
        statusCsvUrlContext.stopPolling();
      }
    }

    // 正常に終了した場合は、CSVファイルをダウンロード
    if (
      status === 'SUCCEEDED' &&
      statusCsvUrlContext &&
      statusCsvUrlContext.data &&
      statusCsvUrlContext.data.pollShopCouponMasterUsageCsv
    ) {
      enqueueSnackbar('集計処理が正常に終了しました。CSVファイルをダウンロードします。', { variant: 'success' });
      setCsvLoading(false);
      if (statusCsvUrlContext.data.pollShopCouponMasterUsageCsv.preSignedUrl)
        downloadCsvFileWithBom(statusCsvUrlContext.data.pollShopCouponMasterUsageCsv.preSignedUrl);
    } else if (status === 'FAILED' || status === 'CANCELLED' || hasError) {
      setCsvLoading(false);
      enqueueSnackbar('集計処理でエラーが発生しました', {
        variant: 'error',
      });
      Bugsnag.notify(statusCsvUrlContext.error || new Error('polling error'));
    }
  }, [
    statusCsvUrlContext.data?.pollShopCouponMasterUsageCsv.status,
    statusCsvUrlContext.data?.pollShopCouponMasterUsageCsv.preSignedUrl,
    statusCsvUrlContext?.error,
  ]);

  // さらに読み込む
  const onFetchMore = useCallback(
    async (data: SearchFormValues) => {
      if (statusContext && statusContext.stopPolling) {
        statusContext?.stopPolling();
      }
      if (organization && statusContext && statusContext.fetchMore && statusContext.variables) {
        setLoading(true);
        setFetching(true);
        await invokeJsonQuery({
          variables: {
            usageStartDate: data.usageStartDate
              ? formatJstDate(new Date(data.usageStartDate).toISOString())
              : undefined,
            usageEndDate: data.usageEndDate ? formatJstDate(new Date(data.usageEndDate).toISOString()) : undefined,
            limit: Limit,
            offset: offset,
          },
        });
      }
    },
    [statusContext, getJsonQueryContext, organization]
  );

  return {
    organization,
    loading,
    onSearch,
    onCsvOut: onCsvOutSubmit,
    csvLoading,
    data: getJsonQueryContext.data?.pollShopCouponMasterUsageJson,
    onFetchMore,
    open,
    setOpen,
    allAcquired,
  };
};
